import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CalendarClock,
  Upload,
  Search,
  Download,
  Loader2,
  CheckCircle2,
  Copy,
  ClipboardList,
  CalendarDays,
  Clock,
  Archive,
  Phone,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * Cooling Tracker — Oct 2026
 *
 * Admin uploads the Indane portal cooling CSV; backend persists `cooling`
 * and `ready` rows into `cooling_consumers`. A daily 02:00 IST cron flips
 * `waiting → ready` on each consumer's `bookable_on` date so the admin
 * opens this page once a day and sees exactly who is booking-ready today.
 *
 * Tabs: Today Ready · Upcoming 7d · All Waiting · Booked (audit).
 */
export default function CoolingTracker() {
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState([]);
  const [agencyId, setAgencyId] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("today"); // today | upcoming | waiting | booked
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [stats, setStats] = useState({ ready_today: 0, waiting: 0, booked: 0, refill_unblock: 0 });
  // Forecast series — "cooling se nikalne wale" preview strip under KPI.
  const [forecast, setForecast] = useState(null);
  const [forecastDays, setForecastDays] = useState(14);
  const fileRef = useRef(null);

  const loadAgencies = async () => {
    try {
      const { data } = await api.get("/groups");
      setAgencies(data);
      // Default: "All Agencies" view (empty agencyId). User can pick a
      // specific agency from either the Upload card dropdown or the
      // compact dropdown next to the tabs.
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const loadStats = async () => {
    try {
      const q = agencyId ? `?agency_id=${encodeURIComponent(agencyId)}` : "";
      const { data } = await api.get(`/cooling/stats${q}`);
      setStats(data);
    } catch (e) {
      // non-fatal — tile badge stays stale
    }
  };

  const loadForecast = async () => {
    try {
      const p = new URLSearchParams({ days: String(forecastDays) });
      if (agencyId) p.set("agency_id", agencyId);
      const { data } = await api.get(`/cooling/forecast?${p.toString()}`);
      setForecast(data);
    } catch (e) {
      setForecast(null);
    }
  };

  const loadRows = async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const q = new URLSearchParams({ window: tab });
      if (agencyId) q.set("agency_id", agencyId);
      if (query.trim()) q.set("search", query.trim());
      const { data } = await api.get(`/cooling/list?${q.toString()}`);
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAgencies(); }, []);
  // Agency switch pe stats AUR list dono refresh ho — KPI strip
  // selected agency ki hi count dikhani chahiye, warna admin ko lagta
  // hai KPI mix hai.
  useEffect(() => { loadStats(); loadRows(); loadForecast(); /* eslint-disable-next-line */ }, [agencyId, tab]);
  // Days-window chip switch (7/14/25) → re-fetch only forecast.
  useEffect(() => { loadForecast(); /* eslint-disable-next-line */ }, [forecastDays]);

  // Debounced search within the active tab.
  useEffect(() => {
    const h = setTimeout(() => { loadRows(); }, 350);
    return () => clearTimeout(h);
    // eslint-disable-next-line
  }, [query]);

  const onUpload = async (file) => {
    if (!file) return;
    // Mismatch warning — if admin has picked a specific agency AND we can
    // peek at the CSV's row 1 "Agency, <name>" header, warn when they
    // differ. Backend ultimately returns `header_mismatch=true` too, but
    // showing a native confirm first stops a bad upload BEFORE it writes.
    if (agencyId) {
      try {
        const peek = await file.slice(0, 400).text();
        const firstLine = peek.split(/\r?\n/)[0] || "";
        const parts = firstLine.split(",");
        const csvName = (parts[1] || "").replace(/^\s*|\s*$/g, "").replace(/^"|"$/g, "");
        const pickedName = agencies.find((a) => a.id === agencyId)?.name || "";
        const low = (s) => (s || "").trim().toLowerCase();
        if (csvName && pickedName && low(csvName) !== low(pickedName)
            && !low(csvName).includes(low(pickedName))
            && !low(pickedName).includes(low(csvName))) {
          const ok = window.confirm(
            `⚠ CSV header says "${csvName}" but you're uploading to "${pickedName}".\n\nContinue anyway?`
          );
          if (!ok) {
            if (fileRef.current) fileRef.current.value = "";
            return;
          }
        }
      } catch { /* non-fatal — backend will catch any header-less CSV */ }
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // When agencyId is blank ("All Agencies" view), the backend auto-
      // detects the target agency from the CSV's row 1 header. Keep the
      // query string empty in that case.
      const url = agencyId
        ? `/cooling/upload?agency_id=${agencyId}`
        : `/cooling/upload`;
      const { data } = await api.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const matchHint = {
        exact:  "✅ Auto-detected",
        fuzzy:  "✅ Fuzzy-matched",
        picked: data.header_mismatch ? "⚠ Uploaded with header mismatch" : "✅ Uploaded",
      }[data.match_type || "picked"];
      toast.success(
        `${matchHint} → ${data.agency_name}\nParsed ${data.parsed}: ${data.cooling_ready} ready + ${data.cooling_waiting} waiting (ignored ${data.ignored_kyc} KYC-pending)`,
        { duration: 7000 },
      );
      // Auto-switch dropdown to the matched agency so the admin
      // immediately sees the data they just uploaded.
      if (data.agency_id && data.agency_id !== agencyId) {
        setAgencyId(data.agency_id);
      } else {
        await loadStats();
        await loadRows();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const markBooked = async (relId) => {
    try {
      await api.post(`/cooling/mark-booked/${relId}`);
      toast.success("Marked as booked");
      await loadStats();
      await loadRows();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const markUnblocked = async (relId) => {
    try {
      await api.post(`/cooling/mark-unblocked/${relId}`);
      toast.success("Marked unblocked — ab normal cooling flow mein hai");
      await loadStats();
      await loadRows();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const markBookedBulk = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Mark ${selected.size} consumer(s) as booked?`)) return;
    try {
      await api.post("/cooling/mark-booked-bulk", {
        relationship_ids: Array.from(selected),
      });
      toast.success(`Marked ${selected.size} booked`);
      setSelected(new Set());
      await loadStats();
      await loadRows();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyRelIds = async () => {
    const ids = selected.size
      ? rows.filter((r) => selected.has(r.relationship_id)).map((r) => r.relationship_id)
      : rows.map((r) => r.relationship_id);
    if (!ids.length) return;
    try {
      await navigator.clipboard.writeText(ids.join("\n"));
      toast.success(`Copied ${ids.length} Rel ID${ids.length > 1 ? "s" : ""}`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const downloadCsv = async () => {
    try {
      const q = new URLSearchParams({ window: tab });
      if (agencyId) q.set("agency_id", agencyId);
      const { data } = await api.get(`/cooling/export?${q.toString()}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cooling_${tab}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleOne = (rid) => {
    const next = new Set(selected);
    next.has(rid) ? next.delete(rid) : next.add(rid);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.relationship_id)));
  };

  // Group upcoming rows by `bookable_on` so the UI shows date headers.
  const grouped = useMemo(() => {
    if (tab !== "upcoming") return null;
    const m = new Map();
    for (const r of rows) {
      const d = r.bookable_on || "—";
      if (!m.has(d)) m.set(d, []);
      m.get(d).push(r);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [rows, tab]);

  // Group rows by agency when "All Agencies" view is active (!agencyId).
  // Each agency becomes a collapsible-ish section with its own count badge.
  const groupedByAgency = useMemo(() => {
    if (agencyId) return null;      // single-agency view — no grouping
    if (tab === "upcoming") return null;  // upcoming has its own date grouping
    const m = new Map();
    for (const r of rows) {
      const key = r.agency_name || "—";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(r);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [rows, tab, agencyId]);

  const todayStr = new Date().toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata",
  });

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y.slice(2)}`;
  };

  const tabs = [
    { key: "today",    label: "Today Ready",    icon: CheckCircle2, color: "text-emerald-700" },
    { key: "upcoming", label: "Upcoming (7d)",  icon: CalendarDays,  color: "text-blue-700" },
    { key: "waiting",  label: "All Waiting",    icon: Clock,          color: "text-amber-700" },
    { key: "booked",   label: "Booked",         icon: Archive,        color: "text-slate-600" },
    { key: "refill_unblock", label: `Refill Unblock${stats.refill_unblock ? ` (${stats.refill_unblock})` : ""}`, icon: ClipboardList, color: "text-[#C8553D]" },
  ];

  return (
    <div className="min-h-screen bg-[#F8F8F3]" data-testid="cooling-tracker-page">
      {/* Header */}
      <div className="bg-white border-b border-[#EDEDE5] px-4 sm:px-6 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button
          onClick={() => navigate("/home")}
          className="p-1.5 rounded hover:bg-[#F0F0EA]"
          data-testid="cooling-back"
          title="Back to home"
        >
          <ArrowLeft className="w-5 h-5 text-[#385C4B]" />
        </button>
        <CalendarClock className="w-5 h-5 text-[#385C4B]" />
        <h1 className="text-base sm:text-lg font-bold text-[#1C1C19]">Cooling Tracker</h1>
        <span className="hidden sm:inline text-xs bg-[#385C4B] text-white px-2 py-0.5 rounded font-mono">
          ADMIN
        </span>
        <span className="ml-auto text-[11px] font-mono text-[#6B6B61]">Today: {todayStr}</span>
      </div>

      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-4 space-y-4">
        {/* Upload card */}
        <div className="bg-white border border-[#EDEDE5] rounded-lg p-3 sm:p-4" data-testid="cooling-upload-card">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#6B6B61] mb-2">
            Upload cooling list
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <select
              value={agencyId}
              onChange={(e) => setAgencyId(e.target.value)}
              className="px-2 py-2 text-sm border border-[#DCDCD3] rounded bg-white min-w-0 sm:min-w-[260px]"
              data-testid="cooling-agency-select"
            >
              <option value="">🌐 All Agencies (combined view)</option>
              {agencies.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => onUpload(e.target.files?.[0])}
              disabled={uploading}
              className="flex-1 text-xs file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-[#385C4B] file:text-white hover:file:bg-[#2D4A3C] file:cursor-pointer disabled:opacity-50"
              data-testid="cooling-upload-input"
            />
            {uploading && (
              <span className="text-xs text-[#6B6B61] flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> parsing…
              </span>
            )}
          </div>
          <p className="text-[11px] text-[#6B6B61] mt-2">
            ℹ Will persist <b>cooling</b> + <b>ready</b> rows only (ignores kyc_pending / not_eligible).
            Re-upload safe — same consumer updates with fresh Bookable date; booked rows stay booked.
            {!agencyId && (
              <span className="block mt-1 text-[#385C4B]">
                🪄 "All Agencies" view mein upload karoge to CSV ke row-1 header se agency auto-detect ho jaayegi.
              </span>
            )}
          </p>
        </div>

        {/* KPI strip */}
        <div data-testid="cooling-kpi-strip">
          <div className="text-[11px] font-mono text-[#385C4B] mb-1.5 truncate" data-testid="cooling-agency-chip">
            📍 Showing: <b>{agencyId
              ? (agencies.find((a) => a.id === agencyId)?.name || "—")
              : "🌐 All Agencies (combined)"}</b>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            <div className="bg-white border border-[#EDEDE5] rounded-lg p-3">
              <div className="text-[10px] font-mono uppercase tracking-wider text-emerald-700">Ready today</div>
              <div className="text-xl sm:text-2xl font-bold text-emerald-700 mt-1" data-testid="kpi-ready">{stats.ready_today}</div>
            </div>
            <div className="bg-white border border-[#EDEDE5] rounded-lg p-3">
              <div className="text-[10px] font-mono uppercase tracking-wider text-amber-700">Waiting</div>
              <div className="text-xl sm:text-2xl font-bold text-amber-700 mt-1" data-testid="kpi-waiting">{stats.waiting}</div>
            </div>
            <div className="bg-white border border-[#EDEDE5] rounded-lg p-3">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-600">Booked</div>
              <div className="text-xl sm:text-2xl font-bold text-slate-700 mt-1" data-testid="kpi-booked">{stats.booked}</div>
            </div>
            <div
              className={`bg-white border rounded-lg p-3 ${
                (stats.refill_unblock || 0) > 0 ? "border-[#C8553D] bg-[#C8553D]/5" : "border-[#EDEDE5]"
              }`}
            >
              <div className="text-[10px] font-mono uppercase tracking-wider text-[#C8553D]">⚠ Refill unblock</div>
              <div className="text-xl sm:text-2xl font-bold text-[#C8553D] mt-1" data-testid="kpi-refill-unblock">{stats.refill_unblock || 0}</div>
            </div>
          </div>
        </div>

        {/* Forecast strip — "cooling se nikalne wale" preview, agency-scoped */}
        <ForecastStrip
          forecast={forecast}
          forecastDays={forecastDays}
          setForecastDays={setForecastDays}
          onPeakClick={() => setTab("upcoming")}
        />

        {/* Tabs */}
        <div className="bg-white border border-[#EDEDE5] rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 border-b border-[#EDEDE5]">
            <div className="flex overflow-x-auto flex-1 min-w-0" data-testid="cooling-tabs">
              {tabs.map((t) => {
                const Icon = t.icon;
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => { setTab(t.key); setQuery(""); }}
                    className={`flex-shrink-0 flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-semibold border-b-2 transition-colors ${
                      active
                        ? "border-[#385C4B] text-[#385C4B] bg-[#F5F5F0]"
                        : "border-transparent text-[#6B6B61] hover:text-[#1C1C19]"
                    }`}
                    data-testid={`cooling-tab-${t.key}`}
                  >
                    <Icon className={`w-4 h-4 ${active ? t.color : ""}`} />
                    {t.label}
                  </button>
                );
              })}
            </div>
            {/* Compact agency filter next to tabs — same source of truth as
                the Upload card dropdown. Keeps the filter in reach when the
                admin is deep inside the list and doesn't want to scroll up. */}
            <select
              value={agencyId}
              onChange={(e) => setAgencyId(e.target.value)}
              className="hidden sm:block mr-2 px-2 py-1 text-xs border border-[#DCDCD3] rounded bg-white max-w-[200px] truncate"
              data-testid="cooling-agency-select-tabs"
              title="Filter by agency"
            >
              <option value="">🌐 All Agencies</option>
              {agencies.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>
          {/* Mobile-only agency filter row — stacks under tabs since the
              inline select is hidden below sm breakpoint. */}
          <div className="sm:hidden px-3 py-2 border-b border-[#EDEDE5] bg-[#FAFAF7]">
            <select
              value={agencyId}
              onChange={(e) => setAgencyId(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-[#DCDCD3] rounded bg-white"
              data-testid="cooling-agency-select-tabs-mobile"
            >
              <option value="">🌐 All Agencies</option>
              {agencies.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>

          {/* Search + bulk strip */}
          <div className="px-3 py-2 border-b border-[#EDEDE5] flex flex-col sm:flex-row sm:items-center gap-2 bg-[#FAFAF7]">
            <div className="flex-1 relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#6B6B61]" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name / mobile / Rel ID…"
                className="w-full pl-7 pr-2 py-1.5 text-xs border border-[#DCDCD3] rounded bg-white"
                data-testid="cooling-search"
              />
            </div>
            <div className="flex items-center gap-1.5">
              {tab !== "booked" && rows.length > 0 && (
                <>
                  <button
                    onClick={toggleAll}
                    className="text-[10px] font-mono uppercase tracking-wider px-2 py-1.5 rounded border border-[#DCDCD3] bg-white hover:border-[#385C4B]"
                    data-testid="cooling-toggle-all"
                  >
                    {selected.size === rows.length ? "Unselect" : "Select all"}
                  </button>
                  <button
                    onClick={markBookedBulk}
                    disabled={selected.size === 0}
                    className="text-[10px] font-mono uppercase tracking-wider px-2 py-1.5 rounded bg-[#385C4B] text-white disabled:opacity-40 hover:bg-[#2D4A3C] flex items-center gap-1"
                    data-testid="cooling-bulk-book"
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    Book {selected.size > 0 ? `(${selected.size})` : ""}
                  </button>
                </>
              )}
              <button
                onClick={copyRelIds}
                disabled={rows.length === 0}
                className="text-[10px] font-mono uppercase tracking-wider px-2 py-1.5 rounded border border-[#DCDCD3] bg-white hover:border-[#385C4B] disabled:opacity-40 flex items-center gap-1"
                data-testid="cooling-copy-ids"
                title="Copy Relationship IDs (selected or all visible)"
              >
                <Copy className="w-3 h-3" /> Copy IDs
              </button>
              <button
                onClick={downloadCsv}
                disabled={rows.length === 0}
                className="text-[10px] font-mono uppercase tracking-wider px-2 py-1.5 rounded border border-[#DCDCD3] bg-white hover:border-[#385C4B] disabled:opacity-40 flex items-center gap-1"
                data-testid="cooling-download-csv"
              >
                <Download className="w-3 h-3" /> CSV
              </button>
            </div>
          </div>

          {/* Rows */}
          <div className="divide-y divide-[#F0F0EA]">
            {loading ? (
              <div className="py-10 text-center text-sm text-[#6B6B61]">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
              </div>
            ) : rows.length === 0 ? (
              <div className="py-10 text-center text-sm text-[#6B6B61]" data-testid="cooling-empty">
                <ClipboardList className="w-6 h-6 inline mb-1 opacity-40" /><br />
                No consumers in this view.
                {tab === "today" && (
                  <div className="text-[11px] mt-1">
                    Upload a cooling CSV above to populate the list.
                  </div>
                )}
              </div>
            ) : tab === "upcoming" && grouped ? (
              grouped.map(([date, items]) => (
                <div key={date} className="px-3 py-2 bg-white" data-testid={`cooling-upcoming-day-${date}`}>
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#385C4B] mb-2">
                    {fmtDate(date)} · {items.length} consumer{items.length > 1 ? "s" : ""}
                  </div>
                  <div className="space-y-1.5">
                    {items.map((r) => <RowCard key={r.relationship_id} r={r} fmtDate={fmtDate} tab={tab} selected={selected} toggleOne={toggleOne} markBooked={markBooked} markUnblocked={markUnblocked} showAgency={true} />)}
                  </div>
                </div>
              ))
            ) : groupedByAgency ? (
              groupedByAgency.map(([agencyName, items]) => (
                <div key={agencyName} className="px-3 py-2 bg-white" data-testid={`cooling-agency-group-${agencyName}`}>
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#385C4B] mb-2 flex items-center gap-2">
                    <span>📍 {agencyName}</span>
                    <span className="bg-[#385C4B]/10 text-[#385C4B] px-1.5 py-0.5 rounded">
                      {items.length} consumer{items.length > 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {items.map((r) => <RowCard key={r.relationship_id} r={r} fmtDate={fmtDate} tab={tab} selected={selected} toggleOne={toggleOne} markBooked={markBooked} markUnblocked={markUnblocked} showAgency={true} />)}
                  </div>
                </div>
              ))
            ) : (
              rows.map((r) => (
                <div key={r.relationship_id} className="px-3 py-2">
                  <RowCard r={r} fmtDate={fmtDate} tab={tab} selected={selected} toggleOne={toggleOne} markBooked={markBooked} markUnblocked={markUnblocked} showAgency={true} />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function RowCard({ r, fmtDate, tab, selected, toggleOne, markBooked, markUnblocked, showAgency = false }) {
  const isReady = r.status === "ready";
  const isBooked = r.status === "booked";
  const isRefillUnblock = r.status === "refill_unblock";
  return (
    <div
      className={`flex items-start gap-2 p-2 rounded border ${
        isRefillUnblock ? "border-[#C8553D] bg-[#C8553D]/5"
        : isReady ? "border-emerald-200 bg-emerald-50/40"
        : isBooked ? "border-slate-200 bg-slate-50"
        : "border-[#EDEDE5] bg-white"
      }`}
      data-testid={`cooling-row-${r.relationship_id}`}
    >
      {!isBooked && !isRefillUnblock && (
        <input
          type="checkbox"
          checked={selected.has(r.relationship_id)}
          onChange={() => toggleOne(r.relationship_id)}
          className="mt-1 flex-shrink-0"
          data-testid={`cooling-check-${r.relationship_id}`}
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[#1C1C19] truncate" title={r.consumer_name}>
          {r.consumer_name}
          {isRefillUnblock && (
            <span className="ml-2 text-[10px] font-mono text-[#C8553D] bg-[#C8553D]/10 px-1.5 py-0.5 rounded align-middle">
              ⚠ Refill Unblock Pending
            </span>
          )}
          {showAgency && r.agency_name && (
            <span className="ml-2 text-[10px] font-mono text-[#385C4B] bg-[#385C4B]/10 px-1.5 py-0.5 rounded align-middle">
              📍 {r.agency_name}
            </span>
          )}
        </div>
        <div className="text-[11px] font-mono text-[#6B6B61] flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
          <span className="inline-flex items-center gap-1">
            <Phone className="w-3 h-3" />
            <a href={`tel:${r.mobile}`} className="hover:text-[#385C4B]">{r.mobile || "—"}</a>
          </span>
          <span className="truncate" title={r.relationship_id}>Rel {r.relationship_id}</span>
          {r.last_delivery && <span>Last {fmtDate(r.last_delivery)}</span>}
          {!isRefillUnblock && (
            <span className={isReady ? "text-emerald-700 font-bold" : ""}>
              Bookable {fmtDate(r.bookable_on)}
            </span>
          )}
          {r.kyc_status && isRefillUnblock && <span>KYC {r.kyc_status}</span>}
          {isBooked && r.booked_at && (
            <span className="text-slate-600">
              ✓ Booked {new Date(r.booked_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}
            </span>
          )}
        </div>
      </div>
      {isRefillUnblock ? (
        <button
          onClick={() => markUnblocked(r.relationship_id)}
          className="flex-shrink-0 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded bg-[#C8553D] text-white hover:bg-[#A3432F] flex items-center gap-1"
          data-testid={`cooling-unblock-${r.relationship_id}`}
          title="Portal pe unblock kar diya? Click to move to cooling waiting"
        >
          <CheckCircle2 className="w-3 h-3" /> Unblocked
        </button>
      ) : !isBooked && (
        <button
          onClick={() => markBooked(r.relationship_id)}
          className="flex-shrink-0 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded bg-[#385C4B] text-white hover:bg-[#2D4A3C] flex items-center gap-1"
          data-testid={`cooling-book-${r.relationship_id}`}
          title="Mark this consumer as booked"
        >
          <CheckCircle2 className="w-3 h-3" /> Booked
        </button>
      )}
    </div>
  );
}

/**
 * ForecastStrip — Admin preview of "cooling se nikalne wale" (how many
 * consumers exit the 25-day cooling period and become bookable each day
 * over the next 7 / 14 / 25 days). Vertical bars sized relative to the
 * window max, plus a totals footer with the peak day.
 */
export function ForecastStrip({ forecast, forecastDays, setForecastDays, onPeakClick, emptyHint = "Upload a cooling CSV above to see the forecast." }) {
  const series = forecast?.series || [];
  const max = series.reduce((m, s) => Math.max(m, s.count), 0);
  const totals = forecast?.totals || {};
  const peak = totals.peak;

  const fmtPeak = (p) => {
    if (!p) return null;
    return `${p.day_short} ${p.day_num}/${p.month_short} → ${p.count}`;
  };

  return (
    <div className="bg-white border border-[#EDEDE5] rounded-lg p-3" data-testid="cooling-forecast-strip">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <TrendingUp className="w-3.5 h-3.5 text-[#385C4B]" strokeWidth={1.75} />
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#385C4B]">
          🔮 Cooling se nikalne wale
        </span>
        <div className="ml-auto flex items-center gap-1">
          {[7, 14, 25].map((d) => (
            <button
              key={d}
              onClick={() => setForecastDays(d)}
              className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded border transition-colors ${
                forecastDays === d
                  ? "bg-[#385C4B] text-white border-[#385C4B]"
                  : "bg-white text-[#6B6B61] border-[#DCDCD3] hover:border-[#385C4B]"
              }`}
              data-testid={`cooling-forecast-days-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {series.length === 0 || max === 0 ? (
        <div className="py-6 text-center text-xs text-[#6B6B61]" data-testid="cooling-forecast-empty">
          <TrendingUp className="w-5 h-5 inline mb-1 opacity-40" strokeWidth={1.75} />
          <div>Agle {forecastDays} din mein koi consumer cooling se nahi nikal raha.</div>
          <div className="text-[10px] mt-1">{emptyHint}</div>
        </div>
      ) : (
        <>
          <div className="flex items-end gap-1 sm:gap-1.5 overflow-x-auto pb-1" data-testid="cooling-forecast-bars">
            {series.map((s) => {
              const h = max > 0 ? Math.max(4, Math.round((s.count / max) * 56)) : 4;
              const isPeak = peak && s.date === peak.date;
              const isToday = s.date === forecast?.today;
              return (
                <div
                  key={s.date}
                  className="flex-shrink-0 flex flex-col items-center"
                  style={{ width: "32px" }}
                  title={`${s.day_short} ${s.day_num}/${s.month_short} → ${s.count} consumers`}
                  data-testid={`cooling-forecast-day-${s.date}`}
                >
                  <div className="text-[9px] font-mono text-[#1C1C19] mb-0.5">{s.count || ""}</div>
                  <div
                    className={`w-full rounded-t transition-colors ${
                      s.count === 0 ? "bg-[#EDEDE5]"
                      : isPeak ? "bg-[#C8553D]"
                      : isToday ? "bg-[#385C4B]"
                      : "bg-[#385C4B]/60"
                    }`}
                    style={{ height: `${h}px` }}
                  />
                  <div className={`text-[9px] font-mono mt-1 ${isToday ? "text-[#385C4B] font-bold" : "text-[#6B6B61]"}`}>
                    {s.day_short}
                  </div>
                  <div className={`text-[9px] font-mono ${isToday ? "text-[#385C4B] font-bold" : "text-[#6B6B61]"}`}>
                    {s.day_num}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-2 pt-2 border-t border-[#EDEDE5] text-[11px] font-mono text-[#6B6B61]">
            <span>
              Next 7d: <b className="text-[#1C1C19]">{totals.next_7 || 0}</b>
            </span>
            <span>
              Next 14d: <b className="text-[#1C1C19]">{totals.next_14 || 0}</b>
            </span>
            <span>
              Window: <b className="text-[#1C1C19]">{totals.window || 0}</b>
            </span>
            {peak && peak.count > 0 && (
              <button
                onClick={onPeakClick}
                className="ml-auto text-[#C8553D] font-semibold hover:underline"
                data-testid="cooling-forecast-peak"
                title="Click to open Upcoming tab"
              >
                Peak: {fmtPeak(peak)}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
